import { useMemo } from 'react';
import { useCheckRateSprint } from '../queries/rate';
import {
  useLoadUserDailyTransactionSum,
  useLoadUserMonthlyTransactionSum
} from '../queries/transactions';
import { getMonetaryValue } from '@src/utils/currency';
import ClientApi from '@src/types/client';
import { MAXIMUM_AMOUNT_TO_SEND } from '@src/constants';

interface IUseGetTransactionLimitsOptions {
  receiveCurrency: string;
}

const useGetTransactionLimits = ({ receiveCurrency }: IUseGetTransactionLimitsOptions) => {
  const { data: dailyTransactionSum, isLoading: dailyTransactionSumLoading } =
    useLoadUserDailyTransactionSum();
  const { data: monthlyTransactionSum, isLoading: monthlyTransactionSumLoading } =
    useLoadUserMonthlyTransactionSum();

  const { data: rateData, isLoading: rateDataLoading } = useCheckRateSprint({
    receiveCurrency,
    sendCurrency: 'USD',
    amount: 1
  });

  const handleCalculation = (
    transactionDataLoading: boolean,
    rateDataLoading: boolean,
    transactionAmount: number,
    rateData: ClientApi.RateType | undefined
  ) => {
    if (rateDataLoading || transactionDataLoading) return 0;

    const dailyAmountUsed = getMonetaryValue(transactionAmount || 0);
    const rate = getMonetaryValue(rateData?.rate || 0);

    return getMonetaryValue(dailyAmountUsed * rate);
  };

  const dailyTransactionAmountUsed = useMemo(() => {
    return handleCalculation(
      dailyTransactionSumLoading,
      rateDataLoading,
      dailyTransactionSum?.AmountUsed || 0,
      rateData
    );
  }, [rateData, dailyTransactionSum, dailyTransactionSumLoading, rateDataLoading]);

  const dailyTransactionTotalAmount = useMemo(() => {
    return handleCalculation(
      monthlyTransactionSumLoading,
      rateDataLoading,
      dailyTransactionSum?.TotalAmount || 0,
      rateData
    );
  }, [rateData, dailyTransactionSum, monthlyTransactionSumLoading, rateDataLoading]);

  const monthlyTransactionAmountUsed = useMemo(() => {
    return handleCalculation(
      monthlyTransactionSumLoading,
      rateDataLoading,
      monthlyTransactionSum?.AmountUsed || 0,
      rateData
    );
  }, [rateData, monthlyTransactionSum, monthlyTransactionSumLoading, rateDataLoading]);

  const monthlyTransactionTotalAmount = useMemo(() => {
    return handleCalculation(
      monthlyTransactionSumLoading,
      rateDataLoading,
      monthlyTransactionSum?.TotalAmount || 0,
      rateData
    );
  }, [rateData, monthlyTransactionSum, monthlyTransactionSumLoading, rateDataLoading]);

  const maximumTransactionAmount = useMemo(() => {
    return handleCalculation(false, rateDataLoading, MAXIMUM_AMOUNT_TO_SEND, rateData);
  }, [rateData, rateDataLoading]);

  return {
    dailyTransactionAmountUsed,
    dailyTransactionTotalAmount,
    monthlyTransactionAmountUsed,
    monthlyTransactionTotalAmount,
    dailyTransactionSumLoading,
    monthlyTransactionSumLoading,
    rateDataLoading,
    maximumTransactionAmount
  };
};

export default useGetTransactionLimits;
